import React, { useMemo, useState } from "react";
import { Link, useNavigate, useLocation, useSearchParams } from "react-router-dom";
import Navbar from "@/components/site/Navbar";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Logo } from "@/components/site/Logo";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { ShieldCheck, LockKey, Certificate } from "@phosphor-icons/react";

function getSignInLabel(loading, mfaStep) {
  if (loading) return "Signing in…";
  if (mfaStep) return "Verify & sign in";
  return "Sign in";
}

function SignInForm() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [form, setForm] = useState({ email: "", password: "", mfa_code: "" });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [mfaStep, setMfaStep] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    const res = await login(form.email, form.password, mfaStep ? form.mfa_code : undefined);
    setLoading(false);
    if (res.ok) {
      toast.success("Welcome back");
      navigate(location.state?.from || "/dashboard");
    } else if (res.mfa_required) {
      setMfaStep(true);
      setError("");
    } else {
      setError(res.error);
    }
  };

  return (
    <form onSubmit={submit} className="space-y-5" data-testid="login-form">
      <div>
        <Label htmlFor="signin-email">Email</Label>
        <Input id="signin-email" type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="mt-1" data-testid="login-email" />
      </div>
      <div>
        <Label htmlFor="signin-password">Password</Label>
        <Input id="signin-password" type="password" required value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className="mt-1" data-testid="login-password" />
      </div>
      {mfaStep && (
        <div data-testid="login-mfa-panel">
          <Label htmlFor="signin-mfa">6-digit MFA code</Label>
          <Input id="signin-mfa" required maxLength={6} value={form.mfa_code} onChange={(e) => setForm({ ...form, mfa_code: e.target.value })} className="mt-1 font-mono" data-testid="login-mfa-code" />
          <div className="text-xs text-slate-500 mt-1">From your authenticator app</div>
        </div>
      )}
      {error && <div className="text-sm text-[#D32F2F] bg-[#D32F2F]/10 rounded px-3 py-2" data-testid="login-error">{error}</div>}
      <Button type="submit" disabled={loading} className="w-full h-11 bg-[#0A2540] hover:bg-[#0A2540]/90" data-testid="login-submit">
        {getSignInLabel(loading, mfaStep)}
      </Button>
      <div className="text-sm text-center">
        <Link to="/forgot-password" className="text-slate-600 hover:text-[#0A2540]" data-testid="login-forgot-link">Forgot password?</Link>
      </div>
    </form>
  );
}

function CreateAccountForm() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    const res = await register(form);
    setLoading(false);
    if (res.ok) {
      toast.success("Account created. Welcome!");
      navigate("/dashboard");
    } else {
      setError(res.error);
    }
  };

  return (
    <form onSubmit={submit} className="space-y-5" data-testid="register-form">
      <div>
        <Label htmlFor="reg-name">Full name</Label>
        <Input id="reg-name" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1" data-testid="register-name" />
      </div>
      <div>
        <Label htmlFor="reg-email">Email</Label>
        <Input id="reg-email" type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="mt-1" data-testid="register-email" />
      </div>
      <div>
        <Label htmlFor="reg-password">Password (min 6 characters)</Label>
        <Input id="reg-password" type="password" required minLength={6} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className="mt-1" data-testid="register-password" />
      </div>
      {error && <div className="text-sm text-[#D32F2F] bg-[#D32F2F]/10 rounded px-3 py-2" data-testid="register-error">{error}</div>}
      <Button type="submit" disabled={loading} className="w-full h-11 bg-[#D32F2F] hover:bg-[#D32F2F]/90" data-testid="register-submit">
        {loading ? "Creating…" : "Create account"}
      </Button>
      <p className="text-xs text-slate-500 text-center leading-relaxed">By creating an account you agree to our Terms and acknowledge our Privacy Policy.</p>
    </form>
  );
}

export default function Auth() {
  const [params] = useSearchParams();
  const defaultTab = useMemo(() => (params.get("tab") === "register" ? "register" : "login"), [params]);
  const [tab, setTab] = useState(defaultTab);

  return (
    <div className="min-h-screen flex flex-col bg-slate-50">
      <Navbar />
      <div className="flex-1 grid lg:grid-cols-2">
        <div className="hidden lg:block relative">
          <img
            src="https://images.unsplash.com/photo-1644088379091-d574269d422f?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzV8MHwxfHNlYXJjaHwxfHxmaW5hbmNpYWwlMjBkYXRhJTIwYWJzdHJhY3QlMjBibHVlfGVufDB8fHx8MTc4NDExMDgwMnww&ixlib=rb-4.1.0&q=85"
            alt=""
            className="absolute inset-0 h-full w-full object-cover"
          />
          <div className="absolute inset-0 bg-[#0A2540]/88 flex flex-col justify-between p-12">
            <div className="bg-white/95 rounded-md p-4 inline-block self-start">
              <Logo size={44} />
            </div>
            <div>
              <h2 className="font-[Chivo] text-4xl lg:text-5xl font-black text-white max-w-md leading-tight">
                U.S. tax filing specialist for NRIs.
              </h2>
              <p className="mt-4 text-slate-200 max-w-md leading-relaxed">
                Audit-defensible filings, IRS notice defense, and a secure client portal — all in one operations layer.
              </p>
              <div className="mt-8 flex flex-wrap gap-4 text-slate-200 text-xs">
                <span className="inline-flex items-center gap-1.5"><ShieldCheck size={14} weight="fill" className="text-[#D32F2F]" /> SOC 2 aligned</span>
                <span className="inline-flex items-center gap-1.5"><LockKey size={14} weight="fill" className="text-[#D32F2F]" /> 256-bit TLS</span>
                <span className="inline-flex items-center gap-1.5"><Certificate size={14} weight="fill" className="text-[#D32F2F]" /> IRS e-File</span>
              </div>
            </div>
          </div>
        </div>

        <div className="flex items-center justify-center p-6 sm:p-8 lg:p-16">
          <div className="w-full max-w-md">
            <div className="text-center lg:text-left mb-8">
              <h1 className="font-[Chivo] text-3xl sm:text-4xl font-black text-[#0A2540]" data-testid="auth-title">
                {tab === "login" ? "Welcome back" : "Open a client account"}
              </h1>
              <p className="text-slate-600 mt-2">
                {tab === "login"
                  ? "Access your returns, notices and documents."
                  : "Free to create. Only pay when a return is filed."}
              </p>
            </div>

            <Tabs value={tab} onValueChange={setTab} className="w-full">
              <TabsList className="grid w-full grid-cols-2 mb-6" data-testid="auth-tabs">
                <TabsTrigger value="login" data-testid="auth-tab-login">Client login</TabsTrigger>
                <TabsTrigger value="register" data-testid="auth-tab-register">Get started</TabsTrigger>
              </TabsList>
              <TabsContent value="login" className="mt-0">
                <SignInForm />
              </TabsContent>
              <TabsContent value="register" className="mt-0">
                <CreateAccountForm />
              </TabsContent>
            </Tabs>
          </div>
        </div>
      </div>
    </div>
  );
}
